import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Download, Loader2, Pencil, Plus, Upload, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { MarkdownEditor } from '../../../components/ui/markdown-editor';
import { MarkdownText } from '../../../components/ui/markdown-text';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import StoryStatusBadge from '../../../components/abnahme/StoryStatusBadge';
import { TestAbdeckung } from '../../../components/abnahme/TestAbdeckung';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  abnahmeApi,
  LAUF_ART_LABEL,
  STORY_STATUS_LABEL,
  STORY_STATUS_ORDER,
  type AbnahmeStatusDto,
  type KatalogDto,
  type LaufArt,
  type StoryDto,
  type StoryStatus,
} from '../../../lib/api/abnahme';

const MERKER = 'sidigo.abnahme.board';

interface Gemerkt {
  zugeklappt?: number[];
  status?: StoryStatus | 'alle';
  archiv?: boolean;
}

/** Browser-Speicher kann fehlen (privates Fenster) — dann eben ohne Gedächtnis. */
const lies = (): Gemerkt => {
  try {
    return JSON.parse(window.localStorage.getItem(MERKER) ?? '{}') as Gemerkt;
  } catch {
    return {};
  }
};
const merke = (wert: Gemerkt) => {
  try {
    window.localStorage.setItem(MERKER, JSON.stringify(wert));
  } catch {
    /* egal */
  }
};

/**
 * Abnahme-Katalog: Epics mit ihren Stories, Stand und offene Tickets je Story.
 *
 * Phase 1 von docs/abnahme-plan.md — der Katalog selbst. Testläufe und die
 * Freigabe durch den Kunden kommen mit den späteren Phasen dazu.
 */
export default function AbnahmePage() {
  const navigate = useNavigate();
  const darfBearbeiten = usePermission(P.abnahme.katalog.katalog_bearbeiten);
  const darfLaeufe = usePermission(P.abnahme.testlaeufe.testlaeufe_ansehen);
  const gemerkt = useRef(lies()).current;

  const [modul, setModul] = useState<AbnahmeStatusDto | null>(null);
  const [katalog, setKatalog] = useState<KatalogDto | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [arbeitet, setArbeitet] = useState(false);
  const [suche, setSuche] = useState('');
  const [statusFilter, setStatusFilter] = useState<StoryStatus | 'alle'>(gemerkt.status ?? 'alle');
  const [archiv, setArchiv] = useState(gemerkt.archiv ?? false);
  const [zugeklappt, setZugeklappt] = useState<number[]>(gemerkt.zugeklappt ?? []);
  const [neuesEpic, setNeuesEpic] = useState<{ key: string; title: string; area: string } | null>(null);
  const [neueStory, setNeueStory] = useState<{ epicId: number; title: string } | null>(null);
  const [epicEntwurf, setEpicEntwurf] = useState<{ id: number; description: string } | null>(null);
  const importFeld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    merke({ zugeklappt, status: statusFilter, archiv });
  }, [zugeklappt, statusFilter, archiv]);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const stand = await abnahmeApi.status();
      setModul(stand);
      if (stand?.enabled) {
        setKatalog(await abnahmeApi.katalog(archiv));
      }
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Der Katalog ließ sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, [archiv]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const epics = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    return (katalog?.epics ?? []).map((epic) => ({
      ...epic,
      sichtbar: epic.stories.filter(
        (story) =>
          (statusFilter === 'alle' || story.status === statusFilter) &&
          (!begriff ||
            story.key.toLowerCase().includes(begriff) ||
            story.title.toLowerCase().includes(begriff) ||
            epic.title.toLowerCase().includes(begriff)),
      ),
    }));
  }, [katalog, suche, statusFilter]);

  const filterAktiv = statusFilter !== 'alle' || !!suche.trim();

  const ausfuehren = async (arbeit: () => Promise<unknown>, erfolg: string) => {
    setArbeitet(true);
    try {
      await arbeit();
      toast.success(erfolg);
      await laden();
      return true;
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.');
      return false;
    } finally {
      setArbeitet(false);
    }
  };

  const exportieren = async () => {
    try {
      const daten = await abnahmeApi.exportieren();
      const blob = new Blob([JSON.stringify(daten, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `abnahme-katalog-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Export fehlgeschlagen.');
    }
  };

  const importieren = async (datei: File) => {
    let daten: Record<string, unknown>;
    try {
      daten = JSON.parse(await datei.text()) as Record<string, unknown>;
    } catch {
      toast.error('Die Datei ist kein gültiges JSON.');
      return;
    }
    await ausfuehren(() => abnahmeApi.importieren(daten), 'Katalog importiert.');
  };

  if (laedt && !katalog) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Katalog wird geladen …
      </div>
    );
  }

  if (modul && !modul.enabled) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
        Die Abnahme ist auf diesem System nicht freigeschaltet (<code>ABNAHME_ENABLED</code>).
      </div>
    );
  }

  const leer = (katalog?.epics.length ?? 0) === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Abnahme-Katalog</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Was fertig sein soll: Epics, User Stories und ihre Akzeptanzkriterien — mit Stand und
            offenen Tickets je Story.
          </p>
        </div>
        {darfBearbeiten && !leer && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setNeuesEpic({ key: '', title: '', area: 'ohne' })}
            >
              <Plus className="h-3.5 w-3.5" /> Epic
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => void exportieren()}>
              <Download className="h-3.5 w-3.5" /> Exportieren
            </Button>
          </div>
        )}
      </div>

      {leer ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Noch kein Katalog</CardTitle>
            <CardDescription>
              Die Erstbefüllung legt je Modul der Rechteverwaltung ein Epic an und je Bereich eine
              Story im Status „Entwurf“ — dazu Kundenportal und Lehrkräfte. Alternativ lässt sich
              ein exportierter Katalog übernehmen, etwa vom Testsystem.
            </CardDescription>
          </CardHeader>
          {darfBearbeiten ? (
            <CardContent className="flex flex-wrap gap-2">
              <Button
                className="gap-1.5"
                disabled={arbeitet}
                onClick={() =>
                  void ausfuehren(() => abnahmeApi.erstbefuellung(), 'Katalog angelegt — jetzt ins Review.')
                }
              >
                <Wand2 className="h-4 w-4" /> Aus der Modulstruktur anlegen
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={arbeitet}
                onClick={() => importFeld.current?.click()}
              >
                <Upload className="h-4 w-4" /> Export übernehmen
              </Button>
              <input
                ref={importFeld}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(event) => {
                  const datei = event.target.files?.[0];
                  event.target.value = '';
                  if (datei) void importieren(datei);
                }}
              />
            </CardContent>
          ) : (
            <CardContent className="text-sm text-muted-foreground">
              Anlegen darf, wer den Katalog bearbeiten darf.
            </CardContent>
          )}
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStatusFilter('alle')}
              className={`rounded-md border px-3 py-1.5 text-sm ${statusFilter === 'alle' ? 'border-primary bg-primary/10' : ''}`}
            >
              Alle <span className="text-muted-foreground">{katalog?.total ?? 0}</span>
            </button>
            {STORY_STATUS_ORDER.map((wert) => (
              <button
                key={wert}
                type="button"
                onClick={() => setStatusFilter(statusFilter === wert ? 'alle' : wert)}
                className={`rounded-md border px-3 py-1.5 text-sm ${statusFilter === wert ? 'border-primary bg-primary/10' : ''}`}
              >
                {STORY_STATUS_LABEL[wert]}{' '}
                <span className="text-muted-foreground">{katalog?.counts[wert] ?? 0}</span>
              </button>
            ))}
          </div>

          <TestsystemStand receivedAt={katalog?.testsystem?.receivedAt ?? null} />
          <LetzteLaeufe katalog={katalog} mitLink={darfLaeufe} />

          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={suche}
              onChange={(event) => setSuche(event.target.value)}
              placeholder="Suche nach Schlüssel oder Titel"
              className="max-w-xs"
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={archiv} onChange={(event) => setArchiv(event.target.checked)} />
              Archiv zeigen
            </label>
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setZugeklappt([])}>
                Alle aufklappen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setZugeklappt((katalog?.epics ?? []).map((epic) => epic.id))}
              >
                Alle zuklappen
              </Button>
            </div>
          </div>

          {(katalog?.orphanTicketKeys.length ?? 0) > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Offene Tickets verweisen auf Stories, die es nicht gibt:{' '}
                {katalog?.orphanTicketKeys.join(', ')}. Schlüssel im Ticket korrigieren.
              </span>
            </div>
          )}

          {neuesEpic && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Neues Epic</CardTitle>
                <CardDescription>
                  Der Schlüssel ist das Präfix aller Stories darunter und lässt sich später nicht
                  mehr ändern.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-[8rem_1fr_14rem_auto] sm:items-end">
                <div className="space-y-1">
                  <Label htmlFor="epic-key">Schlüssel</Label>
                  <Input
                    id="epic-key"
                    value={neuesEpic.key}
                    maxLength={8}
                    placeholder="PERS"
                    onChange={(event) =>
                      setNeuesEpic({ ...neuesEpic, key: event.target.value.toUpperCase().replace(/[^A-Z]/g, '') })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="epic-titel">Titel</Label>
                  <Input
                    id="epic-titel"
                    value={neuesEpic.title}
                    onChange={(event) => setNeuesEpic({ ...neuesEpic, title: event.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Ticket-Bereich</Label>
                  <Select value={neuesEpic.area} onValueChange={(wert) => setNeuesEpic({ ...neuesEpic, area: wert })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ohne">ohne</SelectItem>
                      {(modul?.areas ?? []).map((bereich) => (
                        <SelectItem key={bereich.key} value={bereich.key}>
                          {bereich.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button
                    disabled={arbeitet || neuesEpic.key.length < 2 || !neuesEpic.title.trim()}
                    onClick={async () => {
                      const ok = await ausfuehren(
                        () =>
                          abnahmeApi.epicAnlegen({
                            key: neuesEpic.key,
                            title: neuesEpic.title.trim(),
                            area: neuesEpic.area === 'ohne' ? null : neuesEpic.area,
                          }),
                        `Epic ${neuesEpic.key} angelegt.`,
                      );
                      if (ok) setNeuesEpic(null);
                    }}
                  >
                    Anlegen
                  </Button>
                  <Button variant="ghost" onClick={() => setNeuesEpic(null)}>
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4">
            {epics
              .filter((epic) => !filterAktiv || epic.sichtbar.length > 0)
              .map((epic) => {
                const zu = zugeklappt.includes(epic.id);
                const offen = epic.stories.reduce((summe, story) => summe + story.tickets.open, 0);
                const blocker = epic.stories.reduce((summe, story) => summe + story.tickets.blocker, 0);
                const imNachtest = epic.stories.reduce((summe, story) => summe + (story.tickets.nachtest ?? 0), 0);
                const abgenommen = epic.stories.filter((story) => story.status === 'abgenommen').length;
                return (
                  <Card key={epic.id} className={epic.archived ? 'opacity-60' : undefined}>
                    <button
                      type="button"
                      className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left"
                      onClick={() =>
                        setZugeklappt(zu ? zugeklappt.filter((id) => id !== epic.id) : [...zugeklappt, epic.id])
                      }
                    >
                      {zu ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <Badge variant="outline" className="font-mono">
                        {epic.key}
                      </Badge>
                      <span className="font-medium">{epic.title}</span>
                      {epic.archived && <Badge variant="secondary">archiviert</Badge>}
                      <span className="ml-auto flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>
                          {abgenommen}/{epic.stories.length} abgenommen
                        </span>
                        {offen > 0 && (
                          <span className={blocker > 0 ? 'font-medium text-red-600' : undefined}>
                            {offen} offene Tickets{blocker > 0 ? `, ${blocker} Blocker` : ''}
                          </span>
                        )}
                        {imNachtest > 0 && <span>{imNachtest} im Nachtest</span>}
                      </span>
                    </button>
                    {!zu && (
                      <CardContent className="space-y-3 pt-0">
                        {/* Beschreibung des Epics, Markdown (T-0160). */}
                        {epicEntwurf?.id === epic.id ? (
                          <div className="space-y-2">
                            <MarkdownEditor
                              rows={5}
                              value={epicEntwurf.description}
                              onChange={(wert) => setEpicEntwurf({ id: epic.id, description: wert })}
                              aria-label={`Beschreibung von ${epic.key}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                disabled={arbeitet}
                                onClick={async () => {
                                  const ok = await ausfuehren(
                                    () => abnahmeApi.epicAendern(epic.id, { description: epicEntwurf.description }),
                                    'Epic gespeichert.',
                                  );
                                  if (ok) setEpicEntwurf(null);
                                }}
                              >
                                Speichern
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEpicEntwurf(null)}>
                                Abbrechen
                              </Button>
                            </div>
                          </div>
                        ) : epic.description || darfBearbeiten ? (
                          <div className="flex items-start gap-2">
                            {epic.description ? (
                              <MarkdownText text={epic.description} className="flex-1 text-sm text-muted-foreground" />
                            ) : (
                              <p className="flex-1 text-sm text-muted-foreground">Keine Beschreibung.</p>
                            )}
                            {darfBearbeiten && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 gap-1.5 text-xs"
                                onClick={() => setEpicEntwurf({ id: epic.id, description: epic.description ?? '' })}
                              >
                                <Pencil className="h-3.5 w-3.5" /> Beschreibung
                              </Button>
                            )}
                          </div>
                        ) : null}
                        {epic.sichtbar.length > 0 ? (
                          <StoryTabelle
                            stories={epic.sichtbar}
                            onOpen={(story) => navigate(`/abnahme/stories/${story.id}`)}
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {epic.stories.length === 0 ? 'Noch keine Stories.' : 'Keine Story passt zum Filter.'}
                          </p>
                        )}
                        {darfBearbeiten && !epic.archived &&
                          (neueStory?.epicId === epic.id ? (
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <Textarea
                                autoFocus
                                rows={1}
                                value={neueStory.title}
                                placeholder="Als … möchte ich …, damit …"
                                onChange={(event) => setNeueStory({ ...neueStory, title: event.target.value })}
                              />
                              <div className="flex gap-2">
                                <Button
                                  disabled={arbeitet || !neueStory.title.trim()}
                                  onClick={async () => {
                                    const ok = await ausfuehren(
                                      () =>
                                        abnahmeApi.storyAnlegen({
                                          epicId: epic.id,
                                          title: neueStory.title.trim(),
                                          status: 'geplant',
                                        }),
                                      'Story angelegt.',
                                    );
                                    if (ok) setNeueStory(null);
                                  }}
                                >
                                  Anlegen
                                </Button>
                                <Button variant="ghost" onClick={() => setNeueStory(null)}>
                                  Abbrechen
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1.5"
                              onClick={() => setNeueStory({ epicId: epic.id, title: '' })}
                            >
                              <Plus className="h-3.5 w-3.5" /> Story
                            </Button>
                          ))}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}

function StoryTabelle({
  stories,
  onOpen,
}: {
  stories: StoryDto[];
  onOpen: (story: StoryDto) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">Schlüssel</TableHead>
            <TableHead>Story</TableHead>
            <TableHead className="w-40">Stand</TableHead>
            <TableHead className="w-24 text-right">Kriterien</TableHead>
            <TableHead className="w-44">Tests</TableHead>
            <TableHead className="w-40">Tickets</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stories.map((story) => (
            <TableRow
              key={story.id}
              className={`cursor-pointer ${story.archived ? 'opacity-60' : ''}`}
              onClick={() => onOpen(story)}
            >
              <TableCell className="font-mono text-xs">{story.key}</TableCell>
              <TableCell className="text-sm">
                {story.title}
                {story.archived && <span className="ml-2 text-xs text-muted-foreground">(archiviert)</span>}
              </TableCell>
              <TableCell>
                <StoryStatusBadge status={story.status} />
                {story.regression && (
                  <span
                    className="ml-1.5 inline-flex rounded bg-red-600 px-1.5 py-0.5 text-xs font-medium text-white"
                    title="Abgenommen, aber inzwischen rote Tests oder ein Blocker-Ticket"
                  >
                    Regression
                  </span>
                )}
                {story.readyVersion && (
                  <div className="mt-0.5 text-xs text-muted-foreground">Version {story.readyVersion}</div>
                )}
              </TableCell>
              <TableCell
                className={`text-right text-sm ${story.kriterienCount ? '' : 'text-muted-foreground'}`}
              >
                {story.kriterienCount ?? 0}
              </TableCell>
              <TableCell>
                <TestAbdeckung tests={story.tests} />
              </TableCell>
              <TableCell className="text-sm">
                {story.tickets.open > 0 ? (
                  <span className={story.tickets.blocker > 0 ? 'font-medium text-red-600' : undefined}>
                    {story.tickets.open} offen
                    {story.tickets.blocker > 0 ? ` · ${story.tickets.blocker} Blocker` : ''}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {(story.tickets.refs ?? []).slice(0, 3).join(', ')}
                      {(story.tickets.refs ?? []).length > 3 ? ' …' : ''}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">{nachgelagert(story) ? '' : '—'}</span>
                )}
                {nachgelagert(story) && (
                  <span className="block text-xs text-muted-foreground">{nachgelagert(story)}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Wie alt die Tickets vom Testsystem sind. Der Abgleich läuft alle 15 Minuten;
 * ist er länger still, soll das auffallen statt still veraltete Zahlen zu zeigen.
 */
function TestsystemStand({ receivedAt }: { receivedAt: string | null }) {
  if (!receivedAt) {
    return (
      <p className="text-xs text-muted-foreground">
        Tickets vom Testsystem: noch kein Abgleich — gezählt wird nur dieses System.
      </p>
    );
  }
  const minuten = Math.round((Date.now() - new Date(receivedAt).getTime()) / 60_000);
  const alt = minuten > 60;
  return (
    <p className={`text-xs ${alt ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
      Tickets vom Testsystem: Stand {new Date(receivedAt).toLocaleString('de-DE')}
      {alt ? ' — der Abgleich ist seit über einer Stunde still.' : ''}
    </p>
  );
}

/** Jüngster Lauf je Art in einer Zeile — und ein Hinweis auf verwaiste Tests. */
function LetzteLaeufe({ katalog, mitLink }: { katalog: KatalogDto | null; mitLink: boolean }) {
  const laeufe = Object.entries(katalog?.letzteLaeufe ?? {});
  if (laeufe.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Noch kein Testlauf gemeldet — die Abdeckung erscheint nach dem nächsten Push.
      </p>
    );
  }
  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {laeufe.map(([art, lauf]) =>
        lauf ? (
          <span key={art} className={lauf.fehlgeschlagen > 0 ? 'text-red-600 dark:text-red-400' : undefined}>
            {LAUF_ART_LABEL[art as LaufArt] ?? art}: {lauf.bestanden} grün
            {lauf.fehlgeschlagen > 0 ? `, ${lauf.fehlgeschlagen} rot` : ''}
            {lauf.coverage !== null ? ` · Coverage ${lauf.coverage.toLocaleString('de-DE')} %` : ''}
            {lauf.version ? ` · ${lauf.version}` : ''}
          </span>
        ) : null,
      )}
      {(katalog?.verwaisteTests ?? 0) > 0 && (
        <span className="text-amber-700 dark:text-amber-300">
          {katalog?.verwaisteTests} Schlüssel in Tests ohne Story
        </span>
      )}
      {mitLink && (
        <Link to="/abnahme/testlaeufe" className="underline">
          Testläufe
        </Link>
      )}
    </p>
  );
}

/** „1 im Nachtest · 2 behoben“ — schon ausgerollte Fixes, nachrangig angezeigt. */
function nachgelagert(story: StoryDto): string {
  const teile: string[] = [];
  if (story.tickets.nachtest) teile.push(`${story.tickets.nachtest} im Nachtest`);
  if (story.tickets.behoben) teile.push(`${story.tickets.behoben} behoben`);
  return teile.join(' · ');
}
